import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useNoteList,
  useNoteTags,
  useNoteTypes,
  useTogglePin,
} from "../hooks/useNotes";
import { NoteCard } from "./NoteCard";
import { NewNoteDrawer, type NotePrefill } from "./NewNoteDrawer";

type Props = {
  filter:
    | { companyId: string }
    | { productId: string }
    | { projectId: string }
    | { meetingId: string }
    | { taskId: string }
    | { actionId: string };
  prefill: NotePrefill;
};

export function NotesTab({ filter, prefill }: Props) {
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);

  const { data, isLoading } = useNoteList({
    page: 1,
    limit: 50,
    ...filter,
  });
  const togglePin = useTogglePin();
  const noteTypes = useNoteTypes().data ?? [];
  const noteTagOptions = useNoteTags().data ?? [];

  const tagLabels = useMemo(
    () => Object.fromEntries(noteTagOptions.map((t) => [t.value, t.label])),
    [noteTagOptions],
  );
  const typeLabels = useMemo(
    () => Object.fromEntries(noteTypes.map((t) => [t.value, t.label])),
    [noteTypes],
  );

  const notes = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {notes.length} note{notes.length === 1 ? "" : "s"}
        </div>
        <Button size="sm" onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          New Note
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading notes…
        </div>
      ) : notes.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No notes yet
        </div>
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
        >
          {notes.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              onClick={() => navigate(`/notes/${n.id}`)}
              onTogglePin={() =>
                togglePin.mutate({ id: n.id, isPinned: !n.isPinned })
              }
              tagLabels={tagLabels}
              typeLabels={typeLabels}
            />
          ))}
        </div>
      )}

      <NewNoteDrawer
        isOpen={newOpen}
        onClose={() => setNewOpen(false)}
        prefill={prefill}
      />
    </div>
  );
}
